import {createRule, type Maybe} from '@regle/core';
import {useFormat, useTranslation} from '#imports';
import {useImageDimensionsValidator, type ImageDimensionsMetadata} from './common/dimensions.validator';

/**
 * Validates if an image is within the maximum dimensions.
 * @param value - The image to validate.
 * @param maxWidth - The maximum width of the image.
 * @param maxHeight - The maximum height of the image.
 * @returns True if the image is within the maximum dimensions, false otherwise.
 *
 * @example
 * ```ts
 * const {r$} = useRegle(... , {
 *    image: {maxImageDimensions: maxImageDimensions(400, 200)}
 * })
 * ```
 */
export const maxImageDimensions = createRule({
    async validator(value: Maybe<File>, maxWidth: number, maxHeight: number): Promise<ImageDimensionsMetadata> {
        return await useImageDimensionsValidator({value, widthLimit: maxWidth, heightLimit: maxHeight, type: 'max'});
    },
    message(metadata) {
        const {t} = useTranslation();
        const {formatNumber} = useFormat();

        if (metadata.errorType === 'invalid-image-dimensions') {
            const [widthLimit, heightLimit] = metadata.$params;
            return t('generic.forms.validation.errors.maxImageDimensions', {
                width: formatNumber(metadata.width),
                height: formatNumber(metadata.height),
                maxWidth: formatNumber(widthLimit),
                maxHeight: formatNumber(heightLimit),
            });
        } else if (metadata.errorType === 'image-dimensions-unreadable') {
            return t('generic.forms.validation.errors.image.dimensions.unreadable');
        }
        return t('generic.forms.validation.errors.common');
    },
});
